import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { getCalc } from "../lib/api";
import { useWorkflow } from "./WorkflowContext";
import { clearCardIntent, setCardIntent } from "./cardIntent";   // A2-581 lead 6
import { isSignedOff } from "./calcStatus";   // A2-581 lead 35

/**
 * `?calc_id=<id>` — open that calculation (Review, or Lock & export when
 * already signed/locked). Moved out of WorkflowLayout.jsx (A1-93).
 *
 * History rules (A1-93 (4) and the 2026-10-22 browser journeys):
 *  - The `calc_id` is removed from the address IMMEDIATELY, by replacing
 *    the history entry, before any round trip. Back can then never land on
 *    an address that names a calc, whatever happens to the open.
 *  - The calc being opened is recorded as this tab's calc at the same
 *    moment, so a reload mid-open reopens IT, not the previous one.
 *  - A leftover `calc_id` (reload / Back onto an old address,
 *    bootCalcId.js; or Back/Forward onto one while this screen is open)
 *    never replaces the calc on screen; the operator is told in one line.
 *  - If the open fails or is abandoned, the tab's record goes back to the
 *    calc that is actually on screen — only if it still names ours.
 */
export const useUrlCalcOpen = (setNotice) => {
  const { openCalc, setActiveStep, shouldOpenUrlCalc, noteLayoutMount, calc } = useWorkflow();
  const loc = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const calcRef = useRef(calc);
  calcRef.current = calc;
  const aliveRef = useRef(true);
  const mountedRef = useRef(false);
  const handledKeyRef = useRef(null);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; noteLayoutMount?.(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const firstRun = !mountedRef.current;
    mountedRef.current = true;
    if (handledKeyRef.current === loc.key) return;   // StrictMode re-run
    handledKeyRef.current = loc.key;
    const params = new URLSearchParams(loc.search);
    const calcId = params.get("calc_id");
    if (!calcId) return;
    params.delete("calc_id");
    // A2-581 lead 6: "open card Y on this calc" - kept off the address,
    // answered by the review page only on this calc (workflow/cardIntent)
    const card = params.get("card");
    params.delete("card");
    setCardIntent(calcId, card);
    const rest = params.toString();
    // On the bare /workflow index, WorkflowIndexRedirect already replaces
    // the entry without the calc_id; a second navigation here only races it
    // (round 4: React can commit route transitions out of order).
    if (!/^\/workflow\/?$/.test(loc.pathname)) {
      navigate({ pathname: loc.pathname, search: rest ? `?${rest}` : "" }, { replace: true });
    }

    const loadedId = calcRef.current?.id || null;
    // Back/Forward onto an address naming a calc — while this screen is
    // open, or after leaving it for another screen — is a leftover (an
    // RR-created entry has a real key; a page's first address does not).
    const fromHistory = navType === "POP" && (!firstRun || loc.key !== "default");
    const leftover = !shouldOpenUrlCalc(calcId, { fromHistory }) || (!firstRun && navType === "POP");
    if (leftover) {
      clearCardIntent();   // the linked calc is not opened, so neither is its card
      if (calcId !== loadedId) setNotice({ kind: "kept", id: calcId });
      return;
    }
    const prevSaved = sessionStorage.getItem("vat.calcId");
    sessionStorage.setItem("vat.calcId", calcId);
    const restoreIfOurs = () => {
      clearCardIntent();   // not opened: its card is not either
      if (sessionStorage.getItem("vat.calcId") !== calcId) return;
      const onScreen = calcRef.current?.id || prevSaved;
      if (onScreen) sessionStorage.setItem("vat.calcId", onScreen);
      else sessionStorage.removeItem("vat.calcId");
    };
    (async () => {
      try {
        const c = await getCalc(calcId);
        if (!aliveRef.current) { restoreIfOurs(); return; }   // abandoned (R2-4)
        if (!c) { restoreIfOurs(); setNotice({ kind: "failed", id: calcId }); return; }
        // openCalc() puts the calc's own files, period, client, FX and
        // country filter on the form and loads its review first (A1-93).
        // Left mid-open (e.g. to History while the review loads): drop it,
        // never commit it and pull the operator back into the wizard.
        const opened = await openCalc(c, { shouldCommit: () => aliveRef.current });
        if (!opened || !aliveRef.current) { restoreIfOurs(); return; }
        const doneStage = c.frozen || isSignedOff(c);
        setActiveStep(doneStage ? 6 : 4, { replace: true });
      } catch (_e) {
        restoreIfOurs();
        if (aliveRef.current) setNotice({ kind: "failed", id: calcId });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.key]);
};
